// Validation + normalisation helpers for the three free-text fields
// users paste external links/paths into:
//   • Object  → 3D Inspect URL    (one canonical format)
//   • Insp.   → 3D Inspect URL    (survey URL ± ?objectId=)
//   • Object  → Google Drive path (Windows path containing Shared drives\)
//
// Each helper returns { ok, cleaned, hint }:
//   ok       true on empty input OR a recognised format
//   cleaned  the normalised string the textbox should adopt on blur
//            (trailing slash trimmed, query stripped, drive letter
//            stripped, etc.)
//   hint     short red-text hint shown under the textbox when ok=false
//
// Empty input is always treated as valid — these fields are optional
// at the current stage of the workflow.

// Lowercase, hyphenated, 8-4-4-4-12. Case-insensitive in practice.
const UUID_RE = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

// Strict: https + www.terra-3dinspect.com only. Trailing slash is
// stripped before this regex runs.
const OBJECT_URL_RE = new RegExp(
  `^https://www\\.terra-3dinspect\\.com/objects/${UUID_RE}$`,
);

// Either bare survey URL or survey URL with ?objectId=<uuid> query.
const INSPECTION_URL_RE = new RegExp(
  `^https://www\\.terra-3dinspect\\.com/surveys/${UUID_RE}(\\?objectId=${UUID_RE})?$`,
);

// Captures the base survey URL up to and including the survey UUID,
// dropping any ?objectId= tail. Used to clean Format A → Format B.
const INSPECTION_URL_BASE_RE = new RegExp(
  `^(https://www\\.terra-3dinspect\\.com/surveys/${UUID_RE})`,
);

const URL_HINT = 'Invalid 3D Inspect URL';
const DRIVE_HINT = '\\Shared drives\\ not found in path';

function trimTrailingSlashes(s) {
  return s.replace(/\/+$/, '');
}

export function validateObjectUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return { ok: true, cleaned: '', hint: '' };
  const candidate = trimTrailingSlashes(raw);
  if (OBJECT_URL_RE.test(candidate)) {
    return { ok: true, cleaned: candidate, hint: '' };
  }
  return { ok: false, cleaned: raw, hint: URL_HINT };
}

export function validateInspectionUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return { ok: true, cleaned: '', hint: '' };
  const candidate = trimTrailingSlashes(raw);
  if (!INSPECTION_URL_RE.test(candidate)) {
    return { ok: false, cleaned: raw, hint: URL_HINT };
  }
  // Strip the ?objectId=... tail (Format A) so we always save the
  // canonical bare /surveys/<uuid> form (Format B).
  const m = candidate.match(INSPECTION_URL_BASE_RE);
  return { ok: true, cleaned: m ? m[1] : candidate, hint: '' };
}

// Windows path containing the literal segment "\Shared drives\". On
// success we strip an optional single-letter drive prefix like "G:\"
// so the saved value always starts with "Shared drives\..." — the
// drive letter varies by user and isn't part of the canonical path.
const DRIVE_LETTER_PREFIX_RE = /^[A-Za-z]:\\/;

export function validateDrivePath(input) {
  const raw = (input || '').trim();
  if (!raw) return { ok: true, cleaned: '', hint: '' };
  // Normalise forward slashes some users might paste from a URL.
  let s = raw.replace(/\//g, '\\');
  // The marker the path MUST contain. Case-insensitive match against
  // the user's input, but we keep the canonical "Shared drives" case
  // in the cleaned output.
  const markerIdx = s.toLowerCase().indexOf('\\shared drives\\');
  if (markerIdx < 0) {
    // Allow the leading-no-backslash case "Shared drives\..." as
    // valid too — the marker check above misses it because there's
    // no leading "\". Accept when the string itself starts with the
    // segment (case-insensitive).
    if (!s.toLowerCase().startsWith('shared drives\\')) {
      return { ok: false, cleaned: raw, hint: DRIVE_HINT };
    }
  }
  // Strip a leading drive letter ("G:\", "g:\\") if present so the
  // saved value starts at "Shared drives\".
  s = s.replace(DRIVE_LETTER_PREFIX_RE, '');
  // Lop off anything before the "Shared drives\" segment that isn't
  // the start of the string (handles oddly-pasted paths like
  // "\\server\Shared drives\..."). After the strip above, normalise:
  const idx = s.toLowerCase().indexOf('shared drives\\');
  let cleaned = idx > 0 ? s.slice(idx) : s;
  // Canonicalise the casing of "Shared drives" itself and trim any
  // trailing backslashes so saved paths never end with "\".
  cleaned = cleaned.replace(/^shared drives\\/i, 'Shared drives\\');
  cleaned = cleaned.replace(/\\+$/, '');
  return {
    ok: true,
    cleaned,
    hint: '',
  };
}
